// 钱包类型枚举（避免循环依赖）
export enum WalletType {
  OKX = 'okx',
  PHANTOM = 'phantom',
  PRIVY = 'privy'
}

// 网络类型枚举（避免循环依赖）
export enum NetworkType {
  ETHEREUM = 'ethereum',
  SOLANA = 'solana'
}

// API 基础路径
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8181/sqx_fast'

// 钱包挑战消息请求
export interface WalletChallengeRequest {
  walletAddress: string
  walletType: WalletType
  walletNetwork: NetworkType
}

// 钱包挑战消息响应（扁平化结构）
export interface WalletChallengeResponse {
  code: number
  msg: string
  challengeId: string
  challengeMessage: string
  expireTime: string
}

// 钱包登录请求
export interface WalletLoginRequest {
  challengeId: string
  signature: string
}

// 钱包登录响应
export interface WalletLoginResponse {
  token: string
  expire: number
  user: {
    userId: number
    userName: string
    walletAddress: string
    walletType: string
    walletNetwork: string
    solWalletAddress: string
    evmWalletAddress: string
    // 其他用户信息...
  }
}

// 钱包绑定请求
export interface WalletBindRequest {
  userId: number
  walletAddress: string
  walletType: WalletType
  walletNetwork: NetworkType
  signature?: string
  challengeId?: string
}

// Privy 登录请求
export interface PrivyLoginRequest {
  accessToken: string
}

// API 响应基础结构
export interface ApiResponse<T = any> {
  code: number
  msg: string
  data?: T
  // 支持扁平化返回结构
  [key: string]: any
}

// API 请求函数
class WalletApiService {
  private async request<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return data
  }

  // 生成钱包挑战消息
  async generateChallenge(request: WalletChallengeRequest): Promise<WalletChallengeResponse> {
    return this.request<WalletChallengeResponse>('/app/wallet/challenge', {
      method: 'POST',
      body: JSON.stringify(request)
    }) as Promise<WalletChallengeResponse>
  }

  // 钱包登录
  async walletLogin(request: WalletLoginRequest): Promise<ApiResponse<WalletLoginResponse>> {
    return this.request<WalletLoginResponse>('/app/wallet/login', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  // 绑定钱包
  async bindWallet(request: WalletBindRequest): Promise<ApiResponse<string>> {
    return this.request<string>('/app/wallet/bind', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  // 解绑钱包
  async unbindWallet(request: Pick<WalletBindRequest, 'userId' | 'walletAddress'>): Promise<ApiResponse<string>> {
    return this.request<string>('/app/wallet/unbind', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }

  // Privy 登录（返回结构与钱包登录一致）
  async privyLogin(request: PrivyLoginRequest): Promise<ApiResponse<WalletLoginResponse>> {
    return this.request<WalletLoginResponse>('/app/privy/login', {
      method: 'POST',
      body: JSON.stringify(request)
    })
  }
}

// 导出单例实例
export const walletApiService = new WalletApiService()

// 导出类型
export type { WalletApiService }
